import type * as ESBTypes from '@appTypes/esb';
import { tagTypes } from '@modules/rtk/constants';
import { esbApi } from '@modules/rtk/esb-api';
import tagFactory from '@modules/rtk/utils/tagFactory';

type TagType = {
  id: string | number;
  type: (typeof tagTypes)[keyof typeof tagTypes];
};

export const mbcApi = esbApi.injectEndpoints({
  endpoints: builder => ({
    /**
     * @esbEndpoint GET /advancements/v2/meritBadges/counselors/:memberId/profile
     */
    GetMeritBadgeCounselorProfile: builder.query<
      ESBTypes.GetMeritBadgesCounselorProfileV2Response,
      {
        params?: ESBTypes.GetMeritBadgesCounselorProfileV2QueryParams;
        memberId: string | number;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/meritBadges/counselors/${arg.memberId}/profile`,
        method: 'GET',
        params: arg.params,
      }),
      providesTags: (result, error, arg) => {
        if (error || !result) return [];

        return [
          {
            type: tagTypes.MeritBadgeCounselorProfile,
            id: `${arg.memberId}`,
          },
        ];
      },
    }),

    /**
     * @esbEndpoint POST /advancements/v2/meritBadges/counselors/assignMeritBadges
     */
    assignMeritBadgeCounselorBadges: builder.mutation<
      ESBTypes.PostAssignMeritBadgeCounselorBadgeV2Response,
      {
        payload: ESBTypes.PostAssignMeritBadgeCounselorBadgeV2Request;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/meritBadges/counselors/assignMeritBadges`,
        method: 'POST',
        body: arg.payload,
      }),
      invalidatesTags: (_result, error, arg) => {
        if (error) return [];

        const tagsToInvalidate = arg.payload.map(person => ({
          type: tagTypes.MeritBadgeCounselorProfile,
          id: person.memberId,
        }));

        return tagsToInvalidate;
      },
    }),

    /**
     * @esbEndpoint POST /advancements/v2/meritBadges/counselors/assignToYouth
     */

    assignMBCToYouth: builder.mutation<
      ESBTypes.PostMBCAssignToYouthResponse,
      {
        payload: ESBTypes.PostMBCAssignToYouthRequest;
        invalidateExtra?: TagType[];
      }
    >({
      query: arg => ({
        url: '/advancements/v2/meritBadges/counselors/assignToYouth',
        method: 'POST',
        body: arg.payload,
        // response could be partially ok but we want to fail if any is not assigned
        validateStatus(response) {
          return response.status === 200;
        },
      }),
      invalidatesTags: (_result, error, arg, meta) => {
        const isPartialSuccess =
          error?.status === 207 || meta?.response?.status === 207;
        if (error && !isPartialSuccess) return [];

        let tags: TagType[] = arg.payload.map(item =>
          tagFactory.MeritBadgeCounselorProfile.mbcProfile(
            item.counselorMemberId,
          ),
        );

        if (arg.invalidateExtra?.length) {
          tags = [...tags, ...arg.invalidateExtra];
        }

        return tags;
      },
    }),

    /**
     * @esbEndpoint POST /advancements/v2/meritBadges/counselors/removeFromYouth
     */
    removeCounselorFromYouth: builder.mutation<
      ESBTypes.PostRemoveCounselorFromYouthResponse,
      {
        body: ESBTypes.PostRemoveCounselorFromYouthRequest;
        invalidateExtra?: TagType[];
      }
    >({
      query: arg => ({
        url: '/advancements/v2/meritBadges/counselors/removeFromYouth',
        method: 'POST',
        body: arg.body,
      }),
      invalidatesTags: (result, error, arg) => {
        if (!result || error) return [];

        const tags: TagType[] = [];

        if (arg.invalidateExtra?.length) {
          tags.push(...arg.invalidateExtra);
        }

        const mbcProfileTags = arg.body.flatMap(item => [
          tagFactory.MeritBadgeCounselorProfile.mbcProfile(
            item.counselorMemberId,
          ),
        ]);
        tags.push(...mbcProfileTags);
        return tags;
      },
    }),
    /**
     * @esbEndpoint POST /advancements/v2/meritBadges/counselors/youths/removeMeritBadges
     */
    removeMBCYouthMB: builder.mutation<
      ESBTypes.PostRemoveYouthMBCItemResponse,
      {
        body: ESBTypes.PostRemoveYouthMBCItemRequest;
        extraTags?: TagType[];
      }
    >({
      query: arg => ({
        url: '/advancements/v2/meritBadges/counselors/youths/removeMeritBadges',
        method: 'POST',
        body: arg.body,
      }),
      invalidatesTags: (result, error, arg) => {
        if (!result || error) return [];

        const tags: TagType[] = [
          tagFactory.MeritBadgeCounselorProfile.mbcProfile(
            arg.body.counselorMemberId,
          ),
        ];

        if (arg.extraTags?.length) {
          tags.push(...arg.extraTags);
        }

        return tags;
      },
    }),
    /**
     * @esbEndpoint GET /advancements/v2/meritBadges/counselors/:personGuid/dashboard
     */
    getMBCDashboard: builder.query<
      ESBTypes.GetMBCDashboardResponse,
      {
        personGuidOrMID: string | number;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/meritBadges/counselors/${arg.personGuidOrMID}/dashboard`,
      }),
      providesTags(_result, error, arg) {
        if (error) return [];
        const tags = [
          tagFactory.MBCDashboard.userDashboard(arg.personGuidOrMID),
        ];
        return tags;
      },
    }),

    /**
     * @esbEndpoint POST /advancements/meritBadges/counselors/removeMeritBadges
     */
    removeMeritBadgeCounselorBadge: builder.mutation<
      ESBTypes.PostRemoveMeritBadgeCounselorBadgeV2Response,
      {
        payload: ESBTypes.PostRemoveMeritBadgeCounselorBadgeV2Request;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/meritBadges/counselors/removeMeritBadges`,
        method: 'POST',
        body: arg.payload,
      }),
      invalidatesTags: (_result, error, arg) => {
        if (error) return [];

        const tagsToInvalidate = arg.payload.map(person =>
          tagFactory.MeritBadgeCounselorProfile.mbcProfile(person.memberId),
        );

        return tagsToInvalidate;
      },
    }),

    /**
     * @esbEndpoint POST /advancements/v2/meritBadges/counselors/youths/assigned
     */
    GetCounselorsAssignedToYouths: builder.query<
      ESBTypes.PostCounselorsAssignetToYouthsResponse,
      {
        payload: ESBTypes.PostCounselorsAssignetToYouthsRequest;
      }
    >({
      query: arg => ({
        url: '/advancements/v2/meritBadges/counselors/youths/assigned',
        method: 'POST',
        body: arg.payload,
      }),
      providesTags: (result, error, arg) => {
        if (error || !result) return [];

        const tags: { youthMemberId: string | number; meritBadgeId: number }[] =
          [];
        arg.payload.youthMemberId.forEach(youth => {
          arg.payload.meritBadgeId?.forEach(mbId =>
            tags.push({ youthMemberId: youth, meritBadgeId: mbId }),
          );
        });

        const tagsToInvalidate = tags.map(tag =>
          tagFactory.MBCAssignedToYouth.youthMBC(
            tag.youthMemberId,
            tag.meritBadgeId,
          ),
        );

        return tagsToInvalidate;
      },
    }),

    /**
     * @esbEndpoint POST /advancements/v2/organization/meritBadges/printBlueCard
     */
    postBulkMbcMeritBadgeBlueCards: builder.query<
      ESBTypes.PostBulkMeritBadgeBlueCardsRes,
      {
        payload: ESBTypes.PostBulkMeritBadgeBlueCardsReq;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/organization/meritBadges/printBlueCard`,
        method: 'POST',
        body: arg.payload,
        responseHandler: response => response.blob(),
      }),
    }),

    /**
     * @esbEndpoint GET /advancements/v2/meritBadges/counselors/classes
     */
    GetCounselorClasses: builder.query<
      ESBTypes.GetCounselorClassesResponse,
      {
        params: ESBTypes.GetCounselorClassesQueryParams;
      }
    >({
      query: arg => ({
        url: '/advancements/v2/meritBadges/counselors/classes',
        method: 'GET',
        params: arg.params,
      }),
      providesTags: (_result, _error, arg) =>
        tagFactory.MBCClasses.mbcClasses(arg.params),
    }),

    /**
     * @esbEndpoint POST /advancements/v2/meritBadges/counselors/classes
     */
    PostCreateCounselorClass: builder.mutation<
      ESBTypes.PostCreateCounselorClassResponse,
      {
        payload: ESBTypes.PostCreateCounselorClassRequest;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/meritBadges/counselors/classes`,
        method: 'POST',
        body: arg.payload,
      }),
      invalidatesTags: (_result, error, arg) => {
        if (error) return [];

        const tagsToInvalidate = tagFactory.MBCClasses.mbcClasses(arg.payload);

        return tagsToInvalidate;
      },
    }),

    /**
     * @esbEndpoint POST /advancements/v2/meritBadges/counselors/classes/:counselorClassId
     */
    PutUpdateCounselorClass: builder.mutation<
      ESBTypes.PutUpdateCounselorClassResponse,
      {
        counselorClassId: number;
        payload: ESBTypes.PutUpdateCounselorClassRequest;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/meritBadges/counselors/classes/${arg.counselorClassId}`,
        method: 'PUT',
        body: arg.payload,
      }),
    }),

    /**
     * @esbEndpoint POST /advancements/v2/meritBadges/counselors/classes/:counselorClassId
     */
    DeleteCounselorClass: builder.mutation<
      ESBTypes.DeleteCounselorClassResponse,
      {
        counselorClassId: number;
        payload: Pick<
          ESBTypes.PostCreateCounselorClassRequest,
          'counselorMemberId'
        >;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/meritBadges/counselors/classes/${arg.counselorClassId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, error, arg) => {
        if (error) return [];

        const tagsToInvalidate = tagFactory.MBCClasses.mbcClasses(arg.payload);

        return tagsToInvalidate;
      },
    }),

    /**
     * @esbEndpoint POST /advancements/v2/meritBadges/counselors/classes/:counselorClassId/scouts
     */
    PostAddScoutsToClass: builder.mutation<
      ESBTypes.PostAddScoutsToClassResponse,
      {
        counselorClassId: number;
        payload: ESBTypes.PostAddScoutsToClassRequest;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/meritBadges/counselors/classes/${arg.counselorClassId}/scouts`,
        method: 'POST',
        body: arg.payload,
      }),
    }),

    /**
     * @esbEndpoint POST /advancements/v2/meritBadges/counselors/classes/:counselorClassId/scouts/removeMembership
     */
    PostRemoveScoutsFromClass: builder.mutation<
      ESBTypes.PostRemoveScoutsFromClassResponse,
      {
        counselorClassId: number;
        payload: ESBTypes.PostRemoveScoutsFromClassRequest;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/meritBadges/counselors/classes/${arg.counselorClassId}/scouts/removeMembership`,
        method: 'POST',
        body: arg.payload,
      }),
    }),
  }),
});

export const {
  useGetMeritBadgeCounselorProfileQuery,
  useLazyGetMeritBadgeCounselorProfileQuery,
  useAssignMeritBadgeCounselorBadgesMutation,
  useRemoveMeritBadgeCounselorBadgeMutation,
  useAssignMBCToYouthMutation,
  useRemoveCounselorFromYouthMutation,
  useGetMBCDashboardQuery,
  useLazyGetMBCDashboardQuery,
  useGetCounselorsAssignedToYouthsQuery,
  useRemoveMBCYouthMBMutation,
  useLazyPostBulkMbcMeritBadgeBlueCardsQuery,
  useGetCounselorClassesQuery,
  usePostCreateCounselorClassMutation,
  usePutUpdateCounselorClassMutation,
  useDeleteCounselorClassMutation,
  usePostAddScoutsToClassMutation,
  usePostRemoveScoutsFromClassMutation,
} = mbcApi;
